/**
 * Background-GNSS source — the phone's real GPS kept alive while the app is
 * backgrounded or the screen is locked, plugged into the SAME seam as the
 * simulator and the foreground device watch.
 *
 * The foreground source (DeviceGnssSource) uses the WebView's
 * navigator.geolocation. That freezes the moment Android backgrounds the WebView
 * (Doze), so the fix stream stops, so no alarm is evaluated and the track gets
 * gaps. This source instead drives off @capacitor-community/background-geolocation,
 * which runs a native Android foreground service (with a persistent notification)
 * and keeps delivering locations screen-off and through Doze.
 *
 * It emits the same NMEA (RMC + GGA + GST) as the other sources by reusing
 * deviceFixToSentences, so GnssIngest and everything downstream are untouched:
 * position, the ground-track vector, and continuous track recording all ride the
 * existing wire. Because the plugin delivers in the foreground too, when this
 * source is active it fully replaces the foreground watch for the armed period,
 * so there's no seam-switch at the exact moment the app backgrounds (which is
 * where a switch would drop points).
 *
 * Native only. On the web (or where the plugin is absent) start() reports
 * 'unsupported' and does nothing, so nothing native is pulled into the web path.
 */
import { registerPlugin, Capacitor } from '@capacitor/core';
import type {
  BackgroundGeolocationPlugin,
  Location as BgLocation,
} from '@capacitor-community/background-geolocation';
import { deviceFixToSentences, type DeviceFixSample, type DeviceGnssStatus } from './deviceSource';
import type { PositionSource } from './source';

export interface BackgroundGnssOptions {
  /** Status callback so the UI can show acquiring / live / blocked. */
  onStatus?: (status: DeviceGnssStatus, message?: string) => void;
  /**
   * Per-fix hook, called with each normalised sample the moment it arrives, in
   * addition to the NMEA being emitted. The background alarm evaluator uses this
   * to run the priority checks (anchor drag, CPA) and fire a loud notification
   * screen-off, without having to re-parse the wire.
   */
  onLocation?: (sample: DeviceFixSample) => void;
  /** Title of the persistent foreground-service notification. */
  notificationTitle?: string;
  /** Body of the persistent foreground-service notification. */
  notificationText?: string;
  /**
   * Whether the plugin should raise the OS permission dialogs itself if they're
   * not already granted. The runner usually handles permissions up front and
   * passes false; left true it's a safe fallback. Default true.
   */
  requestPermissions?: boolean;
  /** Injectable for tests; defaults to the registered native plugin. */
  plugin?: BackgroundGeolocationPlugin;
  /** Injectable for tests; defaults to Capacitor.isNativePlatform(). */
  isNative?: boolean;
}

/** Normalise a plugin Location into the shared DeviceFixSample shape. */
export function bgLocationToSample(loc: BgLocation): DeviceFixSample {
  return {
    lat: loc.latitude,
    lon: loc.longitude,
    accuracyM: loc.accuracy,
    speedMs: loc.speed,
    headingDeg: loc.bearing,
    // The plugin can hand back a null time on a synthetic first sample; fall back
    // to wall-clock now so the fix always carries a valid stamp.
    timestampMs: loc.time ?? Date.now(),
  };
}

export class BackgroundGnssSource implements PositionSource {
  readonly kind = 'background';

  private listeners = new Set<(line: string) => void>();
  private prev: { lon: number; lat: number; t: number } | null = null;
  /** The watcher id once addWatcher resolves, or null before/after. */
  private watcherId: string | null = null;
  /** Set true by stop() so a still-pending addWatcher tears itself down. */
  private stopped = false;

  private readonly onStatus?: (status: DeviceGnssStatus, message?: string) => void;
  private readonly onLocation?: (sample: DeviceFixSample) => void;
  private readonly notificationTitle: string;
  private readonly notificationText: string;
  private readonly requestPermissions: boolean;
  private readonly plugin?: BackgroundGeolocationPlugin;
  private readonly isNative: boolean;

  constructor(opts: BackgroundGnssOptions = {}) {
    this.onStatus = opts.onStatus;
    this.onLocation = opts.onLocation;
    this.notificationTitle = opts.notificationTitle ?? 'Ardent Fix — watch active';
    this.notificationText =
      opts.notificationText ??
      'Keeping GNSS alive for your armed alarms and track. Tap to open.';
    this.requestPermissions = opts.requestPermissions ?? true;
    this.isNative = opts.isNative ?? Capacitor.isNativePlatform();
    this.plugin =
      opts.plugin ??
      (this.isNative
        ? registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation')
        : undefined);
  }

  start(): void {
    if (this.watcherId !== null || this.stopped) return;
    if (!this.isNative || !this.plugin) {
      this.onStatus?.('unsupported', 'Background GNSS is only available in the installed Android app.');
      return;
    }
    this.onStatus?.('requesting', 'Starting background location…');
    this.plugin
      .addWatcher(
        {
          backgroundTitle: this.notificationTitle,
          backgroundMessage: this.notificationText,
          requestPermissions: this.requestPermissions,
          // Guarantee up-to-date fixes; we don't want a stale cached location
          // feeding an anchor-drag check.
          stale: false,
          // Every fix: an anchor-drag or CPA check wants each metre of movement,
          // not a decimated stream.
          distanceFilter: 0,
        },
        (location, error) => {
          if (error) {
            // NOT_AUTHORIZED is the background-location permission being absent.
            if (error.code === 'NOT_AUTHORIZED') {
              this.onStatus?.(
                'denied',
                'Background location permission denied. Allow "Allow all the time" for Ardent Fix in Android settings.',
              );
            } else {
              this.onStatus?.('error', error.message || 'Background location error.');
            }
            return;
          }
          if (!location) return;
          const sample = bgLocationToSample(location);
          for (const line of deviceFixToSentences(sample, this.prev)) this.emit(line);
          this.prev = { lon: sample.lon, lat: sample.lat, t: sample.timestampMs };
          this.onStatus?.('live');
          // Run the caller's per-fix hook after NMEA is emitted, so anything it
          // reads from the store reflects this fix.
          try {
            this.onLocation?.(sample);
          } catch {
            // A throwing hook must never kill the location stream.
          }
        },
      )
      .then((id) => {
        // stop() may have been called while addWatcher was in flight: tear down
        // immediately rather than leaking a live watcher (and its service).
        if (this.stopped) {
          void this.plugin?.removeWatcher({ id }).catch(() => undefined);
          return;
        }
        this.watcherId = id;
      })
      .catch((e: unknown) => {
        this.onStatus?.('error', e instanceof Error ? e.message : 'Could not start background location.');
      });
  }

  stop(): void {
    this.stopped = true;
    const id = this.watcherId;
    this.watcherId = null;
    this.prev = null;
    if (id && this.plugin) {
      void this.plugin.removeWatcher({ id }).catch(() => undefined);
    }
    this.onStatus?.('idle');
  }

  onSentence(cb: (line: string) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private emit(line: string): void {
    for (const cb of this.listeners) cb(line);
  }
}
